/** Pure helpers for the `creative_candidate` → drag/drop → `script_generation`
 * → Diverge flow (VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_
 * ASSET_CONTEXT_LINKING_V1). Kept out of `CanvasBoard.tsx` for the same
 * independently-testable reason as `creativeIdeationCanvas.ts` — this
 * module only ever reads/writes plain field objects, the actual HTTP
 * dispatch (`generateScriptGeneration`/`divergeScriptGeneration`) stays
 * in `CanvasBoard.tsx` next to every other API call it already owns. */

import type { ScriptArtifact, ScriptGenerationCreativeContext, ScriptGenerationInputPayload, ScriptGenerationSupportingAssetRef } from '../../../api'
import { normalizeScriptGenerationFieldDefaults } from './scriptGenerationContract'
import { resolveScriptSourceContext } from './scriptSourceContext'
import type { CreativeContextDragPayload } from './types'

/** Canvas edge `relationType` linking a `creative_candidate` node to every
 * `script_generation` node whose Primary Creative Context it became
 * (spec §8 — "必须建立...canonical edge relationship"). Reuses the
 * existing `CanvasRelationType` union value `DERIVED_FROM` rather than
 * inventing a new enum value the backend/typed-dataflow layer would also
 * need to learn about. */
export const CREATIVE_CONTEXT_EDGE_RELATION = 'DERIVED_FROM'

export interface ScriptCreativeContextField extends CreativeContextDragPayload {
  /** Supporting Assets inherited from the source candidate's root Brief
   * at link time (spec §6) — snapshot of REFERENCES only (asset_id /
   * asset_type / display_name / picker_item_id), never a copy of the
   * asset entity itself. */
  linkedAssetRefs: ScriptSupportingAssetField[]
  linkedAt: string
  /** Fingerprint of this context's own content fields AT LINK TIME —
   * compared against a freshly computed fingerprint of the SAME source
   * node (if it still exists) to detect "Creative Idea edited after
   * drop" (spec §9 CASE D) without a re-drop. */
  fingerprint: string
}

export interface ScriptSupportingAssetField {
  asset_id?: string | null
  picker_item_id?: string | null
  asset_type: string
  display_name: string
  /** True only for an asset inherited from the source Creative Idea's
   * Brief at link time (spec §6) — purely a UI affordance (e.g. a small
   * "继承自创意" badge); removing one only ever changes THIS node's own
   * `supportingAssets` selection, never the original Brief. */
  inherited?: boolean
  /** Provenance: inherited from upstream Creative edge vs user-added. */
  source?: 'inherited' | 'manual'
  sourceNodeId?: string | null
  sourceResultId?: string | null
  /** Derived from Creative character_role / product_role text (no asset_id). */
  derivedFrom?: 'character_role' | 'product_role' | null
  /** Identity binding to a narrative entity (character/product). */
  boundEntityName?: string | null
  boundEntityType?: string | null
  boundEntityId?: string | null
  relationship?: string | null
  bindingConfidence?: string | null
  bindingSource?: string | null
  relatedEntityName?: string | null
}

export interface ScriptEntityAssetBinding {
  entity_type: string
  entity_name: string
  entity_id?: string | null
  asset_id: string
  asset_display_name?: string | null
  relationship: string
  confidence?: string | null
  source?: string | null
  related_entity_name?: string | null
}

const IMAGE_ASSET_TYPES = new Set([
  'image_asset',
  'image',
  'photo',
  'character_reference',
  'reference_image',
  'model_photo',
  'media_image',
])

function isImageSupportingAsset(asset: { asset_type?: string }): boolean {
  const at = String(asset.asset_type || '').toLowerCase()
  return IMAGE_ASSET_TYPES.has(at) || at.startsWith('image') || at.endsWith('_image')
}

/** Infer character/product ↔ image bindings from Creative text + assets. */
export function inferEntityAssetBindings(input: {
  title?: string | null
  premise?: string | null
  characterRole?: string | null
  productRole?: string | null
  hook?: string | null
  storyDirection?: string | null
  rationale?: string | null
  supportingAssets?: ScriptSupportingAssetField[]
  namedCharacters?: string[]
  namedProducts?: string[]
}): ScriptEntityAssetBinding[] {
  const assets = input.supportingAssets || []
  const corpus = [
    input.characterRole,
    input.premise,
    input.hook,
    input.storyDirection,
    input.title,
    input.productRole,
    input.rationale,
  ]
    .map((s) => String(s || ''))
    .join(' ')
  const bindings: ScriptEntityAssetBinding[] = []
  const seen = new Set<string>()
  const add = (b: ScriptEntityAssetBinding) => {
    const key = `${b.entity_type}:${b.entity_name}:${b.asset_id}:${b.relationship}`
    if (seen.has(key)) return
    seen.add(key)
    bindings.push(b)
  }

  for (const a of assets) {
    if (a.asset_id && a.boundEntityName) {
      add({
        entity_type: a.boundEntityType || 'character',
        entity_name: a.boundEntityName,
        entity_id: a.boundEntityId || `${a.boundEntityType || 'character'}:${a.boundEntityName}`,
        asset_id: a.asset_id,
        asset_display_name: a.display_name,
        relationship: a.relationship || 'depicts',
        confidence: a.bindingConfidence || 'high',
        source: a.bindingSource || 'explicit_supporting_asset',
        related_entity_name: a.relatedEntityName || null,
      })
    }
  }

  const images = assets.filter((a) => a.asset_id && isImageSupportingAsset(a))
  let primaryChar = input.namedCharacters?.[0] || null
  if (!primaryChar) {
    if (/\bAI\b|AI\s*模特/i.test(corpus)) primaryChar = 'AI'
    else if (corpus.includes('模特')) primaryChar = '模特'
  }
  let primaryProduct = input.namedProducts?.[0] || null
  if (!primaryProduct && /新品|新款|衣服|服装|穿的|穿着|outfit|garment/i.test(corpus)) {
    primaryProduct = /新品|新款/.test(corpus) ? '新品' : '服装'
  }

  const clearPersona = /AI\s*模特|人工智能模特|虚拟模特|数字人|这是.{0,16}模特|模特.{0,12}(照片|图片|形象|参考)|\bAI\b/i.test(
    corpus,
  )
  if (images.length === 1 && primaryChar && (clearPersona || (input.namedCharacters || []).length || corpus.includes('模特'))) {
    const img = images[0]
    add({
      entity_type: 'character',
      entity_name: primaryChar,
      entity_id: `character:${primaryChar}`,
      asset_id: String(img.asset_id),
      asset_display_name: img.display_name,
      relationship: 'depicts',
      confidence: clearPersona ? 'high' : 'medium',
      source: 'upstream_prompt_and_single_image',
    })
    if (primaryProduct && /新品|新款|衣服|服装|穿的|穿着|outfit|garment/i.test(corpus)) {
      add({
        entity_type: 'product',
        entity_name: primaryProduct,
        entity_id: `product:${primaryProduct}`,
        asset_id: String(img.asset_id),
        asset_display_name: img.display_name,
        relationship: 'worn_outfit_visual_reference',
        confidence: 'medium',
        source: 'upstream_prompt_and_single_image',
        related_entity_name: primaryChar,
      })
    }
  }
  return bindings
}

export function applyBindingsToSupportingAssets(
  assets: ScriptSupportingAssetField[],
  bindings: ScriptEntityAssetBinding[],
): ScriptSupportingAssetField[] {
  const byAsset = new Map<string, ScriptEntityAssetBinding[]>()
  for (const b of bindings) {
    const list = byAsset.get(b.asset_id) || []
    list.push(b)
    byAsset.set(b.asset_id, list)
  }
  return assets.map((a) => {
    if (!a.asset_id) return a
    const matched = byAsset.get(a.asset_id) || []
    const charBind = matched.find((b) => b.relationship === 'depicts')
    if (!charBind) return a
    return {
      ...a,
      boundEntityName: charBind.entity_name,
      boundEntityType: charBind.entity_type,
      boundEntityId: charBind.entity_id,
      relationship: charBind.relationship,
      bindingConfidence: charBind.confidence,
      bindingSource: charBind.source,
    }
  })
}

/** Coalesce legacy `fields.assets` image edges into supportingAssets for Script. */
export function coalesceScriptSupportingAssets(fields: Record<string, unknown>): ScriptSupportingAssetField[] {
  const supporting = Array.isArray(fields.supportingAssets)
    ? ([...(fields.supportingAssets as ScriptSupportingAssetField[])])
    : []
  const legacy = Array.isArray(fields.assets) ? (fields.assets as ScriptSupportingAssetField[]) : []
  const keys = new Set(supporting.map((a) => `${a.asset_type}:${a.asset_id || a.display_name}`))
  for (const a of legacy) {
    if (!a || typeof a !== 'object') continue
    const k = `${a.asset_type}:${a.asset_id || a.display_name}`
    if (keys.has(k)) continue
    keys.add(k)
    supporting.push({ ...a, inherited: a.inherited ?? true, source: a.source || 'inherited' })
  }
  return supporting
}

const GENERIC_CHARACTER_LABELS = new Set([
  '主角',
  '配角',
  '男主',
  '女主',
  '角色',
  '人物',
  '旁白',
  '观众',
  '某人',
  '一个人',
  'character',
  'protagonist',
  'someone',
])

/** Leading grammatical particles — name starting with one is an incomplete NP fragment. */
const LEADING_FUNCTION_CHARS = new Set('的了着过和与在把被从到给让向往及或且也又而'.split(''))

const OBJECT_NOUN_HEADS = [
  '茶杯',
  '咖啡杯',
  '马克杯',
  '水杯',
  '手提包',
  '背包',
  '皮包',
  '手机',
  '电脑',
  '相机',
  '手表',
  '耳机',
  '项链',
  '戒指',
  '眼镜',
  '花瓶',
  '茶壶',
  '杯子',
  '碗',
  '盘',
  '瓶',
  '袋',
  '包',
  '箱',
  '盒',
  '灯',
  '镜',
  '伞',
  '帽',
  '鞋',
  '靴',
  '裙',
  '裤',
  '衣服',
  '外套',
  '车',
  '枪',
  '刀',
  '剑',
  '桌',
  '椅',
  '床',
  '沙发',
].sort((a, b) => b.length - a.length)

export function isIncompleteEntityFragment(name: string | null | undefined): boolean {
  const n = String(name ?? '').trim()
  if (!n) return true
  return LEADING_FUNCTION_CHARS.has(n[0])
}

export function looksLikePhysicalObject(name: string | null | undefined): boolean {
  const n = String(name ?? '').trim()
  if (!n || isIncompleteEntityFragment(n)) return false
  return OBJECT_NOUN_HEADS.some((head) => n === head || n.endsWith(head))
}

/** Partition script characters vs key objects for display / downstream.
 * Prefers script.key_objects + typed supporting assets; never keeps
 * particle-leading fragments in the character list. */
export function partitionScriptEntities(
  script: Pick<ScriptArtifact, 'characters' | 'key_objects' | 'entity_asset_bindings'> | null | undefined,
  supportingAssets: Array<{
    asset_type?: string
    display_name?: string
    asset_id?: string | null
    picker_item_id?: string | null
    boundEntityName?: string | null
    boundEntityType?: string | null
    relationship?: string | null
  }> = [],
): {
  characters: string[]
  keyObjects: string[]
  characterReferenceByName: Record<string, { asset_id?: string | null; picker_item_id?: string | null; display_name?: string }>
  productReferenceByName: Record<string, { asset_id?: string | null; picker_item_id?: string | null; display_name?: string; relatedEntityName?: string | null }>
} {
  const characterAssetNames = new Set(
    supportingAssets
      .filter((a) => ['character', 'virtual_ip', 'vip'].includes(String(a.asset_type || '')))
      .map((a) => String(a.display_name || '').trim())
      .filter((n) => n && !isIncompleteEntityFragment(n)),
  )
  const productAssetNames = new Set(
    supportingAssets
      .filter((a) => ['product', 'prop', 'object', 'key_object'].includes(String(a.asset_type || '')))
      .map((a) => String(a.display_name || '').trim())
      .filter((n) => n && !isIncompleteEntityFragment(n)),
  )

  const characters: string[] = []
  const keyObjects: string[] = []
  const addChar = (name: string) => {
    if (!name || isIncompleteEntityFragment(name) || characters.includes(name)) return
    characters.push(name)
  }
  const addObj = (name: string) => {
    if (!name || isIncompleteEntityFragment(name) || keyObjects.includes(name)) return
    if (characterAssetNames.has(name)) return
    keyObjects.push(name)
  }

  for (const raw of script?.characters || []) {
    const name = String(raw || '').trim()
    if (!name || isIncompleteEntityFragment(name)) continue
    if (characterAssetNames.has(name)) {
      addChar(name)
      continue
    }
    if (productAssetNames.has(name) || looksLikePhysicalObject(name)) {
      addObj(name)
      continue
    }
    addChar(name)
  }
  for (const raw of script?.key_objects || []) {
    addObj(String(raw || '').trim())
  }
  for (const name of productAssetNames) {
    addObj(name)
  }

  const characterReferenceByName: Record<string, { asset_id?: string | null; picker_item_id?: string | null; display_name?: string }> = {}
  const productReferenceByName: Record<
    string,
    { asset_id?: string | null; picker_item_id?: string | null; display_name?: string; relatedEntityName?: string | null }
  > = {}

  const bindings = Array.isArray(script?.entity_asset_bindings) ? script!.entity_asset_bindings! : []
  for (const b of bindings) {
    if (!b || typeof b !== 'object') continue
    const name = String(b.entity_name || '').trim()
    const aid = String(b.asset_id || '').trim()
    if (!name || !aid) continue
    const matchAsset = supportingAssets.find((a) => String(a.asset_id || '') === aid)
    if (b.entity_type === 'character' || b.relationship === 'depicts') {
      characterReferenceByName[name] = {
        asset_id: aid,
        picker_item_id: matchAsset?.picker_item_id || (aid ? `sa:${aid}` : null),
        display_name: matchAsset?.display_name || b.asset_display_name || name,
      }
      addChar(name)
    }
    if (b.entity_type === 'product' || b.relationship === 'worn_outfit_visual_reference') {
      productReferenceByName[name] = {
        asset_id: aid,
        picker_item_id: matchAsset?.picker_item_id || (aid ? `sa:${aid}` : null),
        display_name: matchAsset?.display_name || b.asset_display_name || name,
        relatedEntityName: b.related_entity_name || null,
      }
      addObj(name)
    }
  }
  for (const a of supportingAssets) {
    if (a.boundEntityName && a.asset_id && (a.relationship === 'depicts' || a.boundEntityType === 'character')) {
      characterReferenceByName[a.boundEntityName] = {
        asset_id: a.asset_id,
        picker_item_id: a.picker_item_id || (a.asset_id ? `sa:${a.asset_id}` : null),
        display_name: a.display_name,
      }
      addChar(a.boundEntityName)
    }
  }

  return { characters, keyObjects, characterReferenceByName, productReferenceByName }
}

/** Derive named character/product chips from Creative text fields when the
 * Brief has no linked Character/Product asset rows — keeps UI + request
 * aligned with execution grounding (UPSTREAM_CONTEXT_VISIBLE). */
export function deriveNamedEntityAssetsFromCreative(
  payload: Pick<
    CreativeContextDragPayload,
    'title' | 'premise' | 'characterRole' | 'productRole' | 'storyDirection' | 'sourceNodeId' | 'candidateId' | 'sourceRunId'
  >,
): ScriptSupportingAssetField[] {
  const title = String(payload.title ?? '')
  const premise = String(payload.premise ?? '')
  const role = String(payload.characterRole ?? '')
  const productRole = String(payload.productRole ?? '')
  const storyDirection = String(payload.storyDirection ?? '')
  const names: string[] = []
  // Kinship nicknames first — avoid greedy 4-char chunks swallowing 猫哥.
  const kinship = /[\u4e00-\u9fff]{1,2}[哥姐弟妹爷叔伯姨]/g
  const kinshipHits: string[] = []
  for (const text of [title, premise, role]) {
    for (const match of text.match(kinship) || []) {
      let cleaned = match
      while (cleaned && LEADING_FUNCTION_CHARS.has(cleaned[0])) cleaned = cleaned.slice(1)
      if (cleaned && /^[\u4e00-\u9fff]{1,2}[哥姐弟妹爷叔伯姨]$/.test(cleaned)) kinshipHits.push(cleaned)
    }
  }
  for (const match of [...new Set(kinshipHits)].sort((a, b) => a.length - b.length)) {
    if (GENERIC_CHARACTER_LABELS.has(match) || isIncompleteEntityFragment(match)) continue
    if (names.some((k) => k !== match && match.includes(k))) continue
    if (!names.includes(match)) names.push(match)
  }
  const latin = role.match(/[A-Za-z][A-Za-z0-9_\-]{1,31}/g) || []
  for (const match of latin) {
    if (GENERIC_CHARACTER_LABELS.has(match) || names.includes(match)) continue
    if (title.includes(match) || premise.includes(match)) names.push(match)
  }
  const lead = title.match(/^([\u4e00-\u9fff]{2,3})/)
  if (lead && premise.includes(lead[1]) && role.includes(lead[1]) && !GENERIC_CHARACTER_LABELS.has(lead[1]) && !names.includes(lead[1])) {
    names.push(lead[1])
  }
  const out: ScriptSupportingAssetField[] = names.map((display_name) => ({
    asset_type: 'character',
    display_name,
    inherited: true,
    source: 'inherited' as const,
    sourceNodeId: payload.sourceNodeId,
    sourceResultId: payload.candidateId || payload.sourceRunId || null,
    derivedFrom: 'character_role' as const,
  }))
  const excluded = new Set(names)
  const productNames: string[] = []
  const addProduct = (name: string) => {
    const n = name.trim()
    if (!n || excluded.has(n) || GENERIC_CHARACTER_LABELS.has(n) || isIncompleteEntityFragment(n)) return
    if (productNames.some((p) => p.includes(n) || n.includes(p))) {
      // Prefer longer concrete phrase.
      const idx = productNames.findIndex((p) => p.includes(n) || n.includes(p))
      if (idx >= 0 && n.length > productNames[idx].length) productNames[idx] = n
      return
    }
    productNames.push(n)
  }
  const subject = productRole.match(/^([\u4e00-\u9fff]{2,8}?)(?:既是|作为|为|是)/)
  if (subject) addProduct(subject[1])
  const searchTexts = [productRole, premise, storyDirection, title]
  for (const head of OBJECT_NOUN_HEADS) {
    const licensed =
      (productRole && productRole.includes(head)) ||
      (!productRole && (premise.includes(head) || storyDirection.includes(head)))
    if (!licensed) continue
    addProduct(head)
    const re = new RegExp(
      `(?:^|[，。；、\\s]|的)([\\u4e00-\\u9fff]{1,2}${head.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'g',
    )
    for (const text of searchTexts) {
      if (!text || !text.includes(head)) continue
      let m: RegExpExecArray | null
      while ((m = re.exec(text))) {
        if (!isIncompleteEntityFragment(m[1])) addProduct(m[1])
      }
    }
    if (productNames.length >= 4) break
  }
  for (const display_name of productNames) {
    if (out.some((a) => a.display_name === display_name)) continue
    out.push({
      asset_type: 'product',
      display_name,
      inherited: true,
      source: 'inherited',
      sourceNodeId: payload.sourceNodeId,
      sourceResultId: payload.candidateId || payload.sourceRunId || null,
      derivedFrom: 'product_role',
    })
  }
  return out
}

/** The one content fingerprint both a `creative_candidate` node's own
 * live `fields` AND a `script_generation` node's stored `creativeContext`
 * can be hashed with — deliberately keyed by the SAME field names
 * `buildCandidateNodeFields` (creativeIdeationCanvas.ts) already uses, so
 * this function works unmodified on either shape. */
export function computeCreativeContentFingerprint(input: {
  title?: unknown
  premise?: unknown
  hook?: unknown
  tone?: unknown
  storyDirection?: unknown
  productRole?: unknown
  characterRole?: unknown
  estimatedDuration?: unknown
  tags?: unknown
  rationale?: unknown
}): string {
  return JSON.stringify({
    title: String(input.title ?? ''),
    premise: String(input.premise ?? ''),
    hook: String(input.hook ?? ''),
    tone: String(input.tone ?? ''),
    storyDirection: String(input.storyDirection ?? ''),
    productRole: input.productRole ?? null,
    characterRole: input.characterRole ?? null,
    estimatedDuration: input.estimatedDuration ?? null,
    tags: Array.isArray(input.tags) ? [...(input.tags as string[])].sort() : [],
    rationale: String(input.rationale ?? ''),
  })
}

/** Builds the `ScriptCreativeContextField` a `script_generation` node
 * stores on `data.fields.creativeContext` straight from the native DnD
 * payload (spec §3/§4) plus the source candidate's root Brief's own
 * `assets` (spec §6 "资产继承" — read-only reference snapshot, never a
 * copy of the asset entity). Also derives character/product chips from
 * Creative text so UI "关联资产" matches execution grounding. */
export function buildCreativeContextField(
  payload: CreativeContextDragPayload,
  briefAssets: Array<Record<string, unknown>>,
): ScriptCreativeContextField {
  let linkedAssetRefs: ScriptSupportingAssetField[] = (briefAssets || [])
    .filter((a) => a && typeof a === 'object')
    .map((a) => ({
      asset_id: (a.asset_id as string | null | undefined) ?? null,
      picker_item_id: (a.picker_item_id as string | null | undefined) ?? null,
      asset_type: String(a.asset_type ?? 'unknown'),
      display_name: String(a.display_name ?? a.asset_id ?? 'asset'),
      inherited: true,
      source: 'inherited' as const,
      sourceNodeId: payload.sourceNodeId,
      sourceResultId: payload.candidateId || payload.sourceRunId || null,
      boundEntityName: (a.bound_entity_name as string | null | undefined) ?? (a.boundEntityName as string | null | undefined) ?? null,
      boundEntityType: (a.bound_entity_type as string | null | undefined) ?? (a.boundEntityType as string | null | undefined) ?? null,
      relationship: (a.relationship as string | null | undefined) ?? null,
    }))
  const derived = deriveNamedEntityAssetsFromCreative(payload)
  const keys = new Set(linkedAssetRefs.map((a) => `${a.asset_type}:${a.asset_id || a.display_name}`))
  for (const d of derived) {
    const k = `${d.asset_type}:${d.asset_id || d.display_name}`
    if (!keys.has(k)) {
      keys.add(k)
      linkedAssetRefs.push(d)
    }
  }
  const namedCharacters = derived.filter((a) => a.asset_type === 'character').map((a) => a.display_name)
  const namedProducts = derived.filter((a) => a.asset_type === 'product').map((a) => a.display_name)
  const bindings = inferEntityAssetBindings({
    title: payload.title,
    premise: payload.premise,
    characterRole: payload.characterRole,
    productRole: payload.productRole,
    hook: payload.hook,
    storyDirection: payload.storyDirection,
    rationale: payload.rationale,
    supportingAssets: linkedAssetRefs,
    namedCharacters,
    namedProducts,
  })
  linkedAssetRefs = applyBindingsToSupportingAssets(linkedAssetRefs, bindings)
  return {
    ...payload,
    linkedAssetRefs,
    linkedAt: new Date().toISOString(),
    fingerprint: computeCreativeContentFingerprint(payload),
  }
}

function supportingAssetKey(a: ScriptSupportingAssetField | Record<string, unknown>): string {
  return String((a as ScriptSupportingAssetField).asset_id || (a as ScriptSupportingAssetField).display_name || '')
}

/** Merge newly inherited Creative assets into Script supportingAssets.
 * Removes prior inherited assets from `previousSourceNodeId` (replace /
 * disconnect semantics) while preserving manual assets. */
export function mergeSupportingAssetsOnCreativeLink(
  existing: ScriptSupportingAssetField[],
  newlyInherited: ScriptSupportingAssetField[],
  previousSourceNodeId: string | null | undefined,
): ScriptSupportingAssetField[] {
  const kept = existing.filter((a) => {
    const isInherited = a.inherited === true || a.source === 'inherited'
    if (!isInherited) return true
    if (previousSourceNodeId && a.sourceNodeId && a.sourceNodeId === previousSourceNodeId) return false
    // Legacy inherited rows without sourceNodeId: drop on any re-link so
    // they cannot accumulate across Creative A → C replacements.
    if (previousSourceNodeId && !a.sourceNodeId) return false
    return true
  })
  const keys = new Set(kept.map(supportingAssetKey))
  const additions = newlyInherited.filter((a) => !keys.has(supportingAssetKey(a)))
  return [...kept, ...additions]
}

/** Clear Primary Creative Context and remove only assets inherited from
 * that Creative edge (manual assets remain). */
export function clearCreativeContextOnUnlink(
  fields: Record<string, unknown>,
  sourceNodeId: string | null | undefined,
): Record<string, unknown> {
  const existing = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as ScriptSupportingAssetField[])
    : []
  const nextAssets = existing.filter((a) => {
    const isInherited = a.inherited === true || a.source === 'inherited'
    if (!isInherited) return true
    if (sourceNodeId && a.sourceNodeId) return a.sourceNodeId !== sourceNodeId
    // Legacy inherited without provenance: remove with the Creative edge.
    return false
  })
  return {
    ...fields,
    creativeContext: null,
    supportingAssets: nextAssets,
  }
}

function assetKey(a: ScriptSupportingAssetField): string {
  return a.asset_id || `${a.asset_type}:${a.display_name}`
}

/** Fingerprint of every OTHER input that (spec §9/§10/§11) should mark an
 * existing Script Result `needsRegeneration` when changed: Supporting
 * Assets selection, content type / script structure / generation
 * requirements / additional requirements / target duration, and an
 * EXPLICIT Skill/Provider pin. Deliberately excludes bookkeeping fields
 * (`activeRunId`/`runStatus`/generation history/...) the same way
 * `creativeIdeationCanvas.briefFingerprint` excludes them. */
export function computeScriptParamsFingerprint(fields: Record<string, unknown>): string {
  const supportingAssets = Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as ScriptSupportingAssetField[]) : []
  const creativeContext = (fields.creativeContext as ScriptCreativeContextField | null) ?? null
  return JSON.stringify({
    creative: creativeContext ? creativeContext.fingerprint : null,
    creativeSource: creativeContext ? creativeContext.sourceNodeId : null,
    assets: supportingAssets.map(assetKey).sort(),
    contentType: fields.contentType ?? null,
    scriptStructure: fields.scriptStructure ?? null,
    generationRequirements: Array.isArray(fields.generationRequirements) ? [...(fields.generationRequirements as string[])].sort() : [],
    additionalRequirements: String(fields.additionalRequirements ?? ''),
    targetDurationSeconds: fields.targetDurationSeconds ?? null,
    skill: fields.skillSelectionSource === 'EXPLICIT' ? fields.skillKey ?? null : 'AUTO',
    provider: fields.providerSelectionMode === 'EXPLICIT' ? fields.providerOptionId ?? null : 'DEFAULT',
  })
}

/** True whenever this node HAS a Script Result but that result was
 * generated against different inputs than what's on screen right now —
 * covers every trigger in spec §9 (asset/skill/provider/param change)
 * via `paramsFingerprintAtGeneration`, and CASE D (Creative Idea itself
 * edited upstream after being dropped, without a re-drop) via
 * `creativeContextLiveFingerprint` (computed by `CanvasBoard`, see
 * `types.ts`'s own doc comment on that field). */
export function computeNeedsRegeneration(fields: Record<string, unknown>, creativeContextLiveFingerprint: string | undefined): boolean {
  if (!fields.scriptResult) return false
  const paramsAtGen = (fields.paramsFingerprintAtGeneration as string | null) ?? null
  if (paramsAtGen == null) return false
  if (paramsAtGen !== computeScriptParamsFingerprint(fields)) return true
  const creativeContext = (fields.creativeContext as ScriptCreativeContextField | null) ?? null
  if (creativeContext && creativeContextLiveFingerprint && creativeContext.fingerprint !== creativeContextLiveFingerprint) return true
  return false
}

/** Builds the `ScriptGenerationInputPayload` sent to `/generate` and
 * `/diverge` straight from a `script_generation` node's own
 * `data.fields` — the ONE place this snapshot is assembled (mirrors
 * `creativeIdeationCanvas.buildBriefPayload`'s own rationale). */
export function buildScriptInputPayload(fields: Record<string, unknown>): ScriptGenerationInputPayload {
  const scriptSource = resolveScriptSourceContext(fields)
  const creativeContext = fields.creativeContext as ScriptCreativeContextField | null
  let supportingAssets = coalesceScriptSupportingAssets(fields)
  const defaults = normalizeScriptGenerationFieldDefaults(fields)
  const ctx: ScriptGenerationCreativeContext = {
    candidate_id: scriptSource?.candidateId ?? creativeContext?.candidateId ?? null,
    title: scriptSource?.title ?? creativeContext?.title ?? '',
    premise: scriptSource?.premise ?? scriptSource?.text ?? creativeContext?.premise ?? '',
    hook: scriptSource?.hook ?? creativeContext?.hook ?? null,
    tone: scriptSource?.tone ?? creativeContext?.tone ?? null,
    story_direction: scriptSource?.storyDirection ?? creativeContext?.storyDirection ?? null,
    product_role: scriptSource?.productRole ?? creativeContext?.productRole ?? null,
    character_role: scriptSource?.characterRole ?? creativeContext?.characterRole ?? null,
    estimated_duration: scriptSource?.estimatedDuration ?? creativeContext?.estimatedDuration ?? null,
    tags: scriptSource?.tags ?? creativeContext?.tags ?? null,
    rationale: scriptSource?.rationale ?? creativeContext?.rationale ?? null,
  }
  const bindings = inferEntityAssetBindings({
    title: ctx.title,
    premise: ctx.premise,
    characterRole: ctx.character_role,
    productRole: ctx.product_role,
    hook: ctx.hook,
    storyDirection: ctx.story_direction,
    rationale: ctx.rationale,
    supportingAssets,
  })
  supportingAssets = applyBindingsToSupportingAssets(supportingAssets, bindings)
  const assetRefs: ScriptGenerationSupportingAssetRef[] = supportingAssets.map((a) => ({
    asset_id: a.asset_id ?? null,
    asset_type: a.asset_type,
    display_name: a.display_name,
    bound_entity_name: a.boundEntityName ?? null,
    bound_entity_type: a.boundEntityType ?? null,
    bound_entity_id: a.boundEntityId ?? null,
    relationship: a.relationship ?? null,
    binding_confidence: a.bindingConfidence ?? null,
    binding_source: a.bindingSource ?? null,
    related_entity_name: a.relatedEntityName ?? null,
  }))
  return {
    creative_context: ctx,
    supporting_assets: assetRefs.length ? assetRefs : null,
    entity_asset_bindings: bindings.length ? bindings : null,
    content_type: defaults.contentType,
    script_structure: (fields.scriptStructure as string | null) ?? null,
    generation_requirements: Array.isArray(fields.generationRequirements) && (fields.generationRequirements as string[]).length ? (fields.generationRequirements as string[]) : null,
    additional_requirements: (fields.additionalRequirements as string | null) || null,
    target_duration_seconds: defaults.targetDurationSeconds,
  }
}

/** Materializing a Generate result INTO the same node (spec §12) — pushes
 * the current result (if any) into Generation History first, capped so
 * this never grows unbounded across many "再生成" clicks. */
const MAX_GENERATION_HISTORY = 20

export function applyGeneratedScript(
  fields: Record<string, unknown>,
  script: ScriptArtifact,
  runMeta: { runId: string; skillKey: string; skillVersion: string; providerOptionId: string | null },
): Record<string, unknown> {
  const history = Array.isArray(fields.generationHistory) ? (fields.generationHistory as unknown[]) : []
  const nextHistory = fields.scriptResult
    ? [
        {
          script: fields.scriptResult,
          version: fields.scriptResultVersion ?? 1,
          completedAt: new Date().toISOString(),
        },
        ...history,
      ].slice(0, MAX_GENERATION_HISTORY)
    : history
  return {
    ...fields,
    scriptResult: script,
    scriptResultVersion: ((fields.scriptResultVersion as number) || 0) + 1,
    scriptArtifactId: script.script_id,
    generationHistory: nextHistory,
    paramsFingerprintAtGeneration: computeScriptParamsFingerprint(fields),
    lastRunId: runMeta.runId,
    lastRunSkillKey: runMeta.skillKey,
    lastRunSkillVersion: runMeta.skillVersion,
    lastRunProviderOptionId: runMeta.providerOptionId,
  }
}

/** Immediate Diverge placeholder sibling (Canonical Generation Card V3) —
 * B/C created after `/diverge` dispatch succeeds, BEFORE SUCCESS, with
 * `runStatus: 'GENERATING'` and no result yet. TOTAL=3 = origin A +
 * these 2 NEW nodes; no visible sibling edges. */
export function buildScriptDivergePlaceholderFields(
  originFields: Record<string, unknown>,
  meta: {
    divergenceGroupId: string
    siblingIndex: number
    originNodeId: string
    runId: string
  },
): Record<string, unknown> {
  const {
    scriptResult: _scriptResult,
    scriptResultVersion: _v,
    scriptArtifactId: _aid,
    generationHistory: _h,
    paramsFingerprintAtGeneration: _pf,
    activeRunId: _runId,
    runStatus: _runStatus,
    runErrorMessage: _runErr,
    activeDivergeRunId: _dRun,
    divergeRunStatus: _dStatus,
    divergeRunErrorMessage: _dErr,
    ...inheritable
  } = originFields
  return {
    ...inheritable,
    scriptResult: null,
    scriptResultVersion: 0,
    scriptArtifactId: null,
    generationHistory: [],
    paramsFingerprintAtGeneration: null,
    activeRunId: meta.runId,
    runStatus: 'GENERATING',
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    divergenceGroupId: meta.divergenceGroupId,
    divergenceSiblingIndex: meta.siblingIndex,
    divergenceOriginNodeId: meta.originNodeId,
  }
}

/** Fields for a sibling `script_generation` node once a Diverge batch
 * SUCCESS applies ONE script onto B/C — inherits Creative Context /
 * Supporting Assets / params / Provider / Skill from the origin, with
 * its OWN independent Script Result (never a shared reference). */
export function buildDivergeSiblingFields(
  originFields: Record<string, unknown>,
  script: ScriptArtifact,
  runMeta: {
    runId: string
    skillKey: string
    skillVersion: string
    providerOptionId: string | null
    divergenceGroupId?: string
    siblingIndex?: number
    originNodeId?: string
  },
): Record<string, unknown> {
  const base =
    runMeta.divergenceGroupId != null && runMeta.siblingIndex != null && runMeta.originNodeId
      ? buildScriptDivergePlaceholderFields(originFields, {
          divergenceGroupId: runMeta.divergenceGroupId,
          siblingIndex: runMeta.siblingIndex,
          originNodeId: runMeta.originNodeId,
          runId: runMeta.runId,
        })
      : (() => {
          const {
            scriptResult: _scriptResult,
            scriptResultVersion: _v,
            scriptArtifactId: _aid,
            generationHistory: _h,
            paramsFingerprintAtGeneration: _pf,
            activeRunId: _runId,
            runStatus: _runStatus,
            runErrorMessage: _runErr,
            ...inheritable
          } = originFields
          return { ...inheritable }
        })()
  return {
    ...applyGeneratedScript(base, script, runMeta),
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    divergenceGroupId: runMeta.divergenceGroupId ?? null,
    divergenceSiblingIndex: runMeta.siblingIndex ?? null,
    divergenceOriginNodeId: runMeta.originNodeId ?? null,
  }
}

/** Walks `DERIVED_FROM` edges to find (at most one) upstream Creative
 * Idea node feeding a `script_generation` node — used by `CanvasBoard`
 * to compute `creativeContextLiveFingerprint` once per render. */
export { findCreativeContextSourceNode, liveCreativeContextFingerprint } from './scriptSourceContext'
